import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import { animeList, tagOptions, yearOptions, typeOptions, type AnimeType } from "@/mocks/animeData";

type SortKey = "rating" | "year";

export default function Sousuo() {
  const [searchParams] = useSearchParams();
  const initTag = searchParams.get("tag") ?? "";
  const initSort = searchParams.get("sort") ?? "";
  const initQ = searchParams.get("q") ?? "";

  const [keyword, setKeyword] = useState(initQ);
  const [years, setYears] = useState<number[]>([]);
  const [type, setType] = useState<AnimeType | "全部">("全部");
  const [tags, setTags] = useState<string[]>(initTag ? [initTag] : []);
  const [sort, setSort] = useState<SortKey>(initSort === "year" ? "year" : "rating");

  const toggle = <T,>(arr: T[], v: T, set: (n: T[]) => void) =>
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const results = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    let list = animeList.filter((a) => {
      const matchKw =
        !kw ||
        a.title.toLowerCase().includes(kw) ||
        a.original.toLowerCase().includes(kw) ||
        a.tags.some((t) => t.toLowerCase().includes(kw));
      const matchYear = years.length === 0 || years.includes(a.year);
      const matchType = type === "全部" || a.type === type;
      const matchTag = tags.length === 0 || tags.every((t) => a.tags.includes(t));
      return matchKw && matchYear && matchType && matchTag;
    });
    list = [...list].sort((a, b) =>
      sort === "rating" ? b.rating - a.rating : b.year - a.year,
    );
    return list;
  }, [keyword, years, type, tags, sort]);

  return (
    <>
      <Navbar />
      <main className="bg-background-50 pt-16 md:pt-[72px]">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-10">
            <nav aria-label="面包屑" className="text-xs text-foreground-500">
              <a href="/" className="hover:text-primary-600">首页</a>
              <span className="mx-1.5">/</span>
              <span className="text-foreground-800">番剧检索</span>
            </nav>
            <h1 className="mt-3 font-heading text-2xl font-black text-foreground-900 md:text-3xl">
              番剧高级检索
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              按年份、评分、标签与播放形式精准筛选，找到共 {results.length} 部匹配番剧。
            </p>

            <div className="mt-5 flex w-full max-w-2xl flex-col gap-2 rounded-full bg-background-50 p-1.5 sm:flex-row">
              <label htmlFor="kw" className="sr-only">关键词</label>
              <input
                id="kw"
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入番名、原名或标签关键词"
                className="h-11 flex-1 rounded-full bg-transparent px-4 text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
              <button
                type="button"
                onClick={() => setKeyword("")}
                className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-restart-line"></i> 重置
              </button>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 py-8 md:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <aside className="space-y-6 lg:col-span-3">
              <div className="rounded-lg bg-background-100 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
                  <i className="ri-calendar-2-line"></i> 按年份
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {yearOptions.map((y) => (
                    <button
                      key={y}
                      type="button"
                      onClick={() => toggle(years, y, setYears)}
                      className={`inline-flex h-8 items-center whitespace-nowrap rounded-full px-3 text-xs font-medium transition-colors ${
                        years.includes(y)
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-50 text-foreground-600 hover:bg-primary-100"
                      }`}
                    >
                      {y}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-background-100 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
                  <i className="ri-cast-line"></i> 播放形式
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["全部", ...typeOptions].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setType(t as AnimeType | "全部")}
                      className={`inline-flex h-8 items-center whitespace-nowrap rounded-full px-3 text-xs font-medium transition-colors ${
                        type === t
                          ? "bg-accent-500 text-foreground-950"
                          : "bg-background-50 text-foreground-600 hover:bg-accent-100"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-background-100 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
                  <i className="ri-price-tag-3-line"></i> 标签筛选
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {tagOptions.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggle(tags, t, setTags)}
                      className={`inline-flex h-8 items-center whitespace-nowrap rounded-full px-3 text-xs font-medium transition-colors ${
                        tags.includes(t)
                          ? "bg-secondary-500 text-background-50"
                          : "bg-background-50 text-foreground-600 hover:bg-secondary-100"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            <div className="lg:col-span-9">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground-700">排序：</span>
                  {(["rating", "year"] as SortKey[]).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSort(s)}
                      className={`inline-flex h-9 items-center gap-1 whitespace-nowrap rounded-full px-4 text-sm font-medium transition-colors ${
                        sort === s
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-primary-100"
                      }`}
                    >
                      <i className={s === "rating" ? "ri-star-line" : "ri-calendar-line"}></i>
                      {s === "rating" ? "评分排序" : "年份排序"}
                    </button>
                  ))}
                </div>
                <span className="text-xs text-foreground-500">共 {results.length} 部 · 默认按{sort === "rating" ? "评分" : "年份"}降序</span>
              </div>

              {results.length > 0 ? (
                <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                  {results.map((a) => (
                    <AnimeCard key={a.id} anime={a} />
                  ))}
                </div>
              ) : (
                <div className="mt-10 flex flex-col items-center justify-center rounded-lg bg-background-100 py-20 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                    <i className="ri-inbox-line text-2xl"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-800">没有找到匹配的番剧</h3>
                  <p className="mt-2 text-sm text-foreground-500">试试减少筛选条件，或换个关键词吧。</p>
                  <button
                    type="button"
                    onClick={() => {
                      setKeyword("");
                      setYears([]);
                      setType("全部");
                      setTags([]);
                    }}
                    className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-restart-line"></i> 清空全部筛选
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}